import React, { createContext, useContext, useEffect, useRef } from 'react';
import {
  OverlayContainer,
  useOverlay,
  usePreventScroll,
  useModal,
  useDialog,
  mergeProps,
  FocusScope,
  AriaOverlayProps,
} from 'react-aria';
import { useOverlayTriggerState, OverlayTriggerState, OverlayTriggerProps } from 'react-stately';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { AnimatePresence, m } from 'framer-motion';
import clsx from 'clsx';

import { useButton } from '../../hooks/useButton';
import { Button } from 'react-aria-components';

import './modal.css';

const ModalContext = createContext<OverlayTriggerState | null>(null);

const DismissButton = ({
 onPress,
 className,
 label,
}: {
  onPress: () => void;
  className?: string;
  label?: string;
}) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const { buttonProps, isHovered } = useButton(
    {
      onPress: () => {
        onPress();
      },
    },
    ref
  );

  return (
    <button
      ref={ref}
      {...buttonProps}
      className={clsx('dismissButton', { 'dismissButton--hovered': isHovered }, className)}
    >
      {label && <span className="dismissLabel">{label}</span>}
      <IoCloseCircleOutline />
    </button>
  );
};

const DIALOG_DEFAULTS: AriaOverlayProps = {
  isDismissable: true,
};

export const ModalDialog = ({
  children,
  title,
  state,
  modalStyle,
  backdropStyle,
  dismissButtonStyle,
  handleClose,
  dismissButtonLabel,
  hideDismissButton,
  ...props
}: {
  children: React.ReactNode;
  state: OverlayTriggerState;
  title?: string;
  modalStyle?: string;
  backdropStyle?: string;
  dismissButtonStyle?: string;
  isPrintable?: boolean;
  handleClose?: () => void;
  dismissButtonLabel?: string;
  hideDismissButton?: boolean;
} & Omit<AriaOverlayProps, 'isOpen' | 'onClose'>) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { overlayProps, underlayProps } = useOverlay(
    {
      ...DIALOG_DEFAULTS,
      ...props,
      isOpen: state.isOpen,
      onClose: () => {
        state.close();
      },
    },
    ref
  );
  const { modalProps } = useModal();
  const { dialogProps, titleProps } = useDialog({}, ref);

  const handleClosePopover = () => {
    state.close();
    // tslint:disable-next-line:no-unused-expression
    handleClose && handleClose();
  };

  usePreventScroll();

  return (
    <m.div
      className={clsx('cm-backdrop', backdropStyle)}
      {...(underlayProps as any)}
      initial="initial"
      animate="enter"
      exit="exit"
      variants={{
        initial: { opacity: 0 },
        enter: { opacity: 1, transition: { when: 'beforeChildren' } },
        exit: { opacity: 0, transition: { when: 'afterChildren' } },
      }}
    >
      <FocusScope contain restoreFocus autoFocus>
        <m.div
          ref={ref}
          className={clsx('cm-modal', modalStyle)}
          {...(mergeProps(overlayProps, dialogProps, modalProps) as any)}
          variants={{
            initial: { opacity: 0, y: 40 },
            enter: { opacity: 1, y: 0, transition: { duration: 0.2 } },
            exit: { opacity: 0, y: 40, transition: { duration: 0.2 } },
          }}
        >
          {title && (
            <h3 className="title" {...titleProps}>
              {title}
            </h3>
          )}
          {children}
          {!hideDismissButton && (
            <div className="modalActions">
              <DismissButton
                onPress={handleClosePopover}
                className={dismissButtonStyle}
                label={dismissButtonLabel}
              />
            </div>
          )}
        </m.div>
      </FocusScope>
    </m.div>
  );
};

export interface ModalProps {
  children: ((_: OverlayTriggerState) => React.ReactNode) | React.ReactNode;
  title?: string;
  content: React.ReactNode;
  modalStyle?: string;
  backdropStyle?: string;
  dismissButtonStyle?: string;
  onOpenChange?: (isOpen: boolean) => void;
  dialogOptions?: AriaOverlayProps;
  isPrintable?: boolean;
  initialState?: Partial<OverlayTriggerProps>;
  isOpen?: boolean;
  handleClose?: () => void;
  dismissButtonLabel?: string;
  hideDismissButton?: boolean;
}

const DefaultModalTrigger = ({
 state,
 children,
}: {
  state: OverlayTriggerState;
  children: React.ReactNode;
}) => {
  return <Button onPress={state.open}>{children}</Button>;
};

export function useOverlayState() {
  return useContext(ModalContext);
}

export const ModalTrigger = ({ initialState, ...props }: ModalProps) => {
  const state = useOverlayTriggerState({
    ...initialState,
    onOpenChange: props.onOpenChange,
  });

  useEffect(() => {
    if (props.isOpen === undefined) return;

    if (props.isOpen && !state.isOpen) {
     state.open();
    } else if (!props.isOpen && state.isOpen) {
       state.close();
     }
    }, [props.isOpen, state]);

  return (
    <ModalContext.Provider value={state}>
      {typeof props.children === 'function' ? (
        props.children(state)
      ) : (
        <DefaultModalTrigger state={state}>{props.children}</DefaultModalTrigger>
      )}
      <OverlayContainer>
        <AnimatePresence>
          {state.isOpen && (
            <ModalDialog
              title={props.title}
              state={state}
              modalStyle={props.modalStyle}
              handleClose={props.handleClose}
              backdropStyle={props.backdropStyle}
              dismissButtonStyle={props.dismissButtonStyle}
              isPrintable={props.isPrintable}
              dismissButtonLabel={props.dismissButtonLabel}
              hideDismissButton={props.hideDismissButton}
              {...props.dialogOptions}
            >
              {props.content}
            </ModalDialog>
          )}
        </AnimatePresence>
      </OverlayContainer>
    </ModalContext.Provider>
  );
};